import { useInfiniteQuery } from '@tanstack/react-query';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Card, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { quandoEntrou } from './formatos.js';
import { inventarioComPerda, resumoDoInventario } from './produto.js';

/** Linhas por página; "Carregar mais" traz a seguinte. */
const INVENTARIOS_POR_PAGINA = 25;

/**
 * A aba **Inventários** de `#v-estoque`: as contagens fechadas, da mais
 * recente para trás.
 *
 * A coluna **Perda** só aparece quando o servidor mandou a perda. Ela é
 * calculada a custo, e a projeção a tirou do `SELECT` de quem não vê custo:
 * o dado NÃO VEIO, e o cabeçalho some junto — uma coluna de traços ensina a
 * pessoa que o sistema está quebrado, e não que ela não tem permissão.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function AbaDeInventarios() {
  const inventarios = useInfiniteQuery({
    queryKey: ['estoque', 'inventarios'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      estoqueApi.inventarios({
        limite: INVENTARIOS_POR_PAGINA,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = inventarios.data?.pages.flatMap((pagina) => pagina.itens) ?? [];
  const comPerda = inventarioComPerda(itens);

  return (
    <Card titulo="Inventários">
      {inventarios.isPending && <EsqueletoDeTabela colunas={5} />}
      {inventarios.isError && (
        <Erro
          detalhe={mensagemDoErro(inventarios.error)}
          onTentarDeNovo={() => void inventarios.refetch()}
        />
      )}
      {inventarios.data && itens.length === 0 && <Vazio>Nenhum inventário fechado.</Vazio>}
      {itens.length > 0 && (
        <>
          <Tabela
            colunas={[
              'Data',
              'Produtos contados',
              'Resultado',
              ...(comPerda ? ['Perda'] : []),
              'Justificativa',
            ]}
          >
            {itens.map((inventario) => (
              <tr key={inventario.id}>
                <td>{quandoEntrou(inventario.fechadoEm)}</td>
                <td className="num">{inventario.itensContados}</td>
                <td>
                  <Status situacao={inventario.divergencias === 0 ? 'ok' : 'atencao'}>
                    {resumoDoInventario(inventario)}
                  </Status>
                </td>
                {comPerda && (
                  <td className="num">
                    {inventario.perdaValor === undefined ? '—' : dinheiro(inventario.perdaValor)}
                  </td>
                )}
                <td>{inventario.justificativa ?? '—'}</td>
              </tr>
            ))}
          </Tabela>
          {inventarios.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void inventarios.fetchNextPage()}
                disabled={inventarios.isFetchingNextPage}
              >
                {inventarios.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
